import { useEffect, useMemo, useState } from 'react';
import './OrderLifeCycle.scss';
import OrderLifeCycleBody from './components/OrderLifeCycleBody/OrderLifeCycleBody';
import OrderLifeCycleHeader from './components/OrderLifeCycleHeader/OrderLifeCycleHeader';
import OrderLifeCycleState from './components/OrderLifeCycleState/OrderLifeCycleState';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import axios from 'axios';
import dayjs from 'dayjs';
import { ActionTypeAdmin } from './types/ActionType';
import { MessageType } from '../../models/MessageType';
import { OrderStateType } from './types/OrderStateType';
import { OrderDetailsType } from './types/OrderDetailsType';
import { useLocation } from 'react-router-dom';
import { Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { useOrderLifeCycleContext } from '../../store/OrderLifeCycleContext';
import { OrderSource } from './types/OrderSource';
import { useResilientStompClient } from '../../hooks/useResilientStompClient';

const DATE_TIME_FORMAT = 'MMM D, YYYY h:mm:ss A';
function OrderLifeCycle() {
	const [notFound, setNotFound] = useState('');
	const [otpVlaid, setOtpValid] = useState({ value: true });
	const [orderDetails, setOrderDetails] = useState<OrderDetailsType | null>(null);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const location = useLocation();
	const { newAssignee } = useOrderLifeCycleContext();

	const orderNumber = useMemo(() => {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get('id') ?? '';
	}, [location.search]);

	const chatStompHeaders = useMemo(() => ({ orderNumber }), [orderNumber]);

	const userName = useMemo(() => orderDetails?.merchant?.fullName || '', [orderDetails]);

	const { stompClient } = useResilientStompClient({
		url: API_ENDPOINTS.chat,
		headers: chatStompHeaders,
		onConnect: () => {},
		onDisconnect: () => {},
	});

	useEffect(() => {
		axios
			.get(API_ENDPOINTS.orderDetails.replace('%orderNumber%', orderNumber))
			.then((response) => setOrderDetails(response.data))
			.catch((err: any) => {
				if (err?.response?.status === 404) setNotFound(err);
			});
	}, [location.search, orderNumber, newAssignee]);

	const getOrderDetails = () => {
		axios
			.get(API_ENDPOINTS.orderDetails.replace('%orderNumber%', orderNumber))
			.then((response) => setOrderDetails(response.data))
			.catch((err: any) => {
				if (err?.response?.status === 404) setNotFound(err);
			});
	};

	const triggerAction = (action: ActionTypeAdmin, otp?: string) => {
		setLoading(true);
		const payload = otp ? { otp } : {};
		axios
			.post(API_ENDPOINTS.orderAction.replace('%orderNumber%', orderNumber).replace('%action%', action), payload)
			.then(() => {
				const actionMessage = { adminAction: action, type: MessageType.ACTION };
				otp && setOtpValid({ value: true });

				if (stompClient?.connected) {
					stompClient.send(
						API_ENDPOINTS.sendOrderChatMsgTopic.replace('%orderNumber%', orderNumber),
						{},
						JSON.stringify(actionMessage),
					);
				}
			})
			.catch((err: any) => {
				if (err?.response?.data.code === 2037 || err?.response?.data?.code === 2046) setOtpValid({ value: false });
				getOrderDetails();
			})
			.finally(() => setLoading(false));
	};

	return (
		<div className='order-life-cycle-page'>
			{notFound ? (
				<Result status='404' title={t<string>('notFound')} />
			) : (
				<>
					<OrderLifeCycleHeader
						orderType={orderDetails?.type || 'BUY'}
						user={userName}
						orderNumber={orderNumber}
						openTime={dayjs(orderDetails?.createdAt).format(DATE_TIME_FORMAT)}
					/>
					<OrderLifeCycleState
						orderState={orderDetails?.status as OrderStateType}
						appealTask={orderDetails?.appealTask ?? null}
						isMerchant={false}
						orderType={orderDetails?.type || 'BUY'}
						expireRemainingDuration={orderDetails?.expireRemainingDuration ?? 0}
						client={orderDetails?.clientDetails?.clientFullName ?? ''}
						merchant={orderDetails?.merchant?.fullName ?? ''}
						merchantId={orderDetails?.merchant?.merchantId ?? 0}
						orderNo={orderNumber}
						getOrderDetails={getOrderDetails}
						clientTotalOrder={orderDetails?.clientDetails?.totalOrders ?? 0}
						merchantTotalOrder={orderDetails?.merchant?.totalOrders ?? 0}
						clientCompletedOrder={orderDetails?.clientDetails?.ordersCompleted || 0}
						merchantCompletedOrder={orderDetails?.merchant?.ordersCompleted || 0}
						merchantReviews={orderDetails?.merchant?.totalReviews ?? 0}
						merchantRating={orderDetails?.merchant?.rating ?? 0}
						merchantData={orderDetails?.merchant}
						clientData={orderDetails?.clientDetails}
						triggerAction={triggerAction}
						source={orderDetails?.source || OrderSource.TRADING_ACCOUNT}
						complained={orderDetails?.complained ?? false}
						cancelReason={orderDetails?.cancelReason ?? null}
					/>
					<OrderLifeCycleBody
						loading={loading}
						orderNumber={orderNumber}
						isMerchant={false}
						orderState={orderDetails?.status as OrderStateType}
						userName={userName}
						client={orderDetails?.clientDetails?.clientFullName ?? ''}
						note={orderDetails?.note || ''}
						orders={orderDetails?.merchant?.totalOrders || 0}
						reviews={orderDetails?.merchant?.totalReviews || 0}
						rating={orderDetails?.merchant?.rating || 0}
						feedback={orderDetails?.review ?? null}
						cancelReason={orderDetails?.cancelReason ?? null}
						orderBody={{
							unitPrice: `${orderDetails?.price?.toLocaleString()}`,
							quantity: `${orderDetails?.quantity?.toLocaleString()}`,
							amount: `${orderDetails?.amount?.toLocaleString()}`,
							paymentMehodId: orderDetails?.paymentMethod || 0,
							accountNumber: `${orderDetails?.accountDetails?.number}`,
							accountGroup: `${orderDetails?.accountDetails?.accountGroup}`,
						}}
						actions={orderDetails?.adminActions || []}
						orderType={orderDetails?.type || 'BUY'}
						merchantId={`${orderDetails?.merchant?.merchantId}` || ''}
						clientId={`${orderDetails?.clientDetails?.clientId}` || ''}
						triggerAction={triggerAction}
						stompClient={stompClient}
						getOrderDetails={getOrderDetails}
						clientOrder={orderDetails?.clientDetails?.totalOrders ?? 0}
						orderHistory={orderDetails?.orderHistory || []}
						appealTask={orderDetails?.appealTask ?? null}
						paymentProof={orderDetails?.paymentProof ?? false}
						paymentProofFileId={orderDetails?.paymentProofFileId ?? ''}
						paymentProofFileLabel={orderDetails?.paymentProofFileLabel ?? ''}
						otpVlaid={otpVlaid}
						source={orderDetails?.source || OrderSource.TRADING_ACCOUNT}
					/>
				</>
			)}
		</div>
	);
}

export default OrderLifeCycle;
